import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Stack,
  Avatar,
  Divider,
  Paper,
  IconButton,
  colors
} from '@mui/material';
import {
  Home,
  Apartment,
  Article,
  People,
  CalendarToday,
  TrendingUp,
  MoreVert
} from '@mui/icons-material';
import { Chart as ChartJS, registerables } from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import { useTheme } from '@mui/material/styles';
import { useGetDashboardSummaryQuery } from './api/settingsApi';
import DashboardSkeleton from './components/dashboard_skeloton';

ChartJS.register(...registerables);

const MetricCard = ({ title, value, icon, color }: any) => {
  return (
    <Card sx={{ height: '100%', borderRadius: 2, boxShadow: 3 }}>
      <CardContent>
        <Stack direction="row" justifyContent="space-between">
          <Avatar sx={{ 
            bgcolor: `${color}.100`, 
            color: `${color}.600`,
            width: 48,
            height: 48
          }}>
            {icon}
          </Avatar>
          <IconButton size="small">
            <MoreVert />
          </IconButton>
        </Stack>
        <Typography variant="subtitle2" color="text.secondary" mt={1}>
          {title}
        </Typography>
        <Typography variant="h4" fontWeight="bold">
          {value}
        </Typography>
  
      </CardContent>
    </Card>
  );
};

const Dashboard = () => {
  const theme = useTheme();
  const { data: dashboardData, isLoading, isError } = useGetDashboardSummaryQuery();

  if (isLoading) return <DashboardSkeleton/>;
  if (isError) return <div style={{ color: 'red' }}>Error loading dashboard data</div>;
  if (!dashboardData) return <div>No data available</div>;

  // Format metrics data from API
  const metrics = [
    { 
      title: 'Total Projects', 
      value: dashboardData.totalProjects, 
      icon: <Home fontSize="medium" />, 
      color: 'primary',
      change: 0 // No change data in API
    },
    { 
      title: 'Total Units', 
      value: dashboardData.totalUnits, 
      icon: <Apartment fontSize="medium" />, 
      color: 'secondary',
      change: 0 // No change data in API
    },
    { 
      title: 'Blog Articles', 
      value: dashboardData.totalBlogs, 
      icon: <Article fontSize="medium" />, 
      color: 'warning',
      change: 0 // No change data in API
    },
    { 
      title: 'Subscribers', 
      value: dashboardData.totalSubscribers, 
      icon: <People fontSize="medium" />, 
      color: 'info',
      change: 0 // No change data in API
    }
  ];

  // Prepare chart data from API
  const months = Object.keys(dashboardData.visitsPerMonth);
  const visitsData = Object.values(dashboardData.visitsPerMonth);
  const subscribersDataValues = Object.values(dashboardData.subscribersPerMonth);

  // Site visits data
  const siteVisitsData = {
    labels: months,
    datasets: [{
      label: 'Scheduled Visits',
      data: visitsData,
      backgroundColor: theme.palette.primary.main,
      borderColor: theme.palette.primary.dark,
      tension: 0.1,
      fill: true
    }]
  };

  // Subscribers data
  const subscribersData = {
    labels: months,
    datasets: [{
      label: 'New Subscribers',
      data: subscribersDataValues,
      backgroundColor: theme.palette.secondary.main,
      borderColor: theme.palette.secondary.dark,
      tension: 0.1,
      fill: true
    }]
  };

  // Recent activity data
  const recentActivities = [
    {
      title: `${dashboardData.newVisitSchedules} new visit ${dashboardData.newVisitSchedules === 1 ? 'schedule' : 'schedules'}`,
      icon: <CalendarToday />,
      time: 'Recently'
    },
    {
      title: `${dashboardData.newSubscribers} new ${dashboardData.newSubscribers === 1 ? 'subscriber' : 'subscribers'}`,
      icon: <People />,
      time: 'Recently'
    },
    {
      title: `Unit added to ${dashboardData.unitsPerProject.project_name}`,
      icon: <Apartment />,
      time: new Date(dashboardData.unitsPerProject.created_at).toLocaleDateString()
    }
  ];

  return (
    <Box sx={{ p: 3, backgroundColor: 'background.default', minHeight: '100vh' }}>
      <Typography variant="h4" component="h1" fontWeight="bold" gutterBottom>
        LBC
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" mb={4}>
        Overview of your website platform performance
      </Typography>

      {/* Key Metrics Cards */}
      <Grid container spacing={3} mb={4}>
        {metrics.map((metric, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <MetricCard {...metric} />
          </Grid>
        ))}
      </Grid>

      {/* Charts Section */}
      <Grid container spacing={3}>
        {/* Site Visits Chart */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', borderRadius: 2, boxShadow: 3 }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" fontWeight="bold">
                  Scheduled Site Visits
                </Typography>
                <IconButton size="small">
                  <MoreVert />
                </IconButton>
              </Stack>
              <Box sx={{ height: 300 }}>
                <Line 
                  data={siteVisitsData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: 'top',
                      },
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                        grid: {
                          color: theme.palette.divider
                        }
                      },
                      x: {
                        grid: {
                          color: theme.palette.divider
                        }
                      }
                    }
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Subscribers Chart */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', borderRadius: 2, boxShadow: 3 }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" fontWeight="bold">
                  Subscriber Growth
                </Typography>
                <IconButton size="small">
                  <MoreVert />
                </IconButton>
              </Stack>
              <Box sx={{ height: 300 }}>
                <Bar 
                  data={subscribersData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: 'top',
                      },
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                        grid: {
                          color: theme.palette.divider
                        }
                      },
                      x: {
                        grid: {
                          color: theme.palette.divider
                        }
                      }
                    }
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;


